import React, {
  useCallback, useEffect, useMemo, useState, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../../components/PageTitle';
import useInputState from '../../helpers/hooks/useInputState';
import { getEventsRequest, cancelEventsRequest } from '../../store/actions/events';
import EventItem from './components/EventItem';
import EventModal from './components/EventModal';
import EventsCheckboxDropdown from './components/EventsCheckboxDropdown';
import {
  EVENT_CATEGORY_NONE,
  EVENT_FILTERS,
  EVENT_MODAL_STATES,
  EVENT_SOURCES,
} from './constants';
import MyButton from '../../components/MyButton';
import AddOrgIcon from '../../assets/formIcons/addOrg.svg';
import MyTable from '../../components/MyTable';
import BulkEditSelectedModal from '../../components/BulkEditSelectedModal';
import './css/index.scss';
import Api from '../../Api';

function buildFilterParam(selectedIds) {
  if (!selectedIds.length) return undefined;
  return selectedIds.join(',');
}

function Events() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    events,
    eventsTotal,
    eventsAllTotal,
    eventsActiveTotal,
    eventsBlockedTotal,
    eventsLoading,
  } = useSelector((store) => store.events);

  const [page, setPage] = useInputState(1);
  const [perPage, setPerPage] = useState(100);
  const [selectedFilter, setSelectedFilter] = useState(EVENT_FILTERS[0]);
  const [q, setQ] = useInputState();
  const [isInit, setIsInit] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalState, setModalState] = useState('');
  const [selectedEvent, setSelectedEvent] = useState({});
  const [itemsPassed, setItemsPassed] = useState(0);
  const [deepSearch, setDeepSearch] = useState(false);
  const abortControllerRef = useRef(null);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);

  const getEvents = ({ paramsPage, paramsPerPage = perPage, isInitRequired = true }) => {
    if (isInitRequired && !isInit) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const params = {
      q,
      page: paramsPage,
      per_page: paramsPerPage,
      is_active: selectedFilter.id,
      sort_by_date: 'false',
      count_all_totals: 'true',
      deep_search: deepSearch ? 'true' : 'false',
      deep_populate: 'true',
    };

    const sources = buildFilterParam(selectedSources);
    const categories = buildFilterParam(selectedCategories);

    if (sources) params.sources = sources;
    if (categories) params.categories = categories;

    dispatch(getEventsRequest(params, abortController));
    setSelectedEvents([]);
  };

  const handleSearch = () => {
    setPage(1);
    getEvents({ paramsPage: 1 });
  };

  const handleCancelSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    dispatch(cancelEventsRequest());
  };

  const toggleSource = (sourceId) => {
    setSelectedSources((prev) => (
      prev.includes(sourceId)
        ? prev.filter((id) => id !== sourceId)
        : [...prev, sourceId]
    ));
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories((prev) => (
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    ));
  };

  const extraFilters = useMemo(() => (
    <>
      <EventsCheckboxDropdown
        label="Источник"
        options={EVENT_SOURCES}
        selectedIds={selectedSources}
        onToggle={toggleSource}
      />
      <EventsCheckboxDropdown
        label="Категория"
        options={categoryOptions}
        selectedIds={selectedCategories}
        onToggle={toggleCategory}
      />
    </>
  ), [categoryOptions, selectedSources, selectedCategories]);

  const TableItemComponent = useCallback((item, index) => (
    <EventItem
      key={item._id}
      item={item}
      itemsPassed={itemsPassed}
      index={index}
      setSelectedEvent={setSelectedEvent}
      setModalState={setModalState}
      setIsModalVisible={setIsModalVisible}
      withCheckbox={selectedEvents.length > 0}
      isSelected={selectedEvents.includes(item._id)}
      setSelected={setSelectedEvents}
    />
  ), [itemsPassed, selectedEvents]);

  const WhileSelectingComponents = useCallback(() => (
    selectedEvents?.length ? (
      <div className="table_selection_bar">
        <span className="table_selection_bar_count">
          Выбрано:
          {' '}
          {selectedEvents.length}
        </span>
        <div
          className="pressable table_selection_bar_action"
          onClick={() => setSelectedEvents(events.map((item) => item._id))}
        >
          Выбрать все на странице
        </div>
        <div
          className="pressable table_selection_bar_action"
          onClick={() => setSelectedEvents([])}
        >
          Снять выбор со всех
        </div>
        <div
          onClick={() => setIsBulkEditOpen(true)}
          className="pressable table_selection_bar_action"
        >
          Редактировать
        </div>
        <div
          onClick={() => {
            setModalState(EVENT_MODAL_STATES.block);
            setIsModalVisible(true);
          }}
          className="pressable table_selection_bar_action"
        >
          Забанить
        </div>
        <div
          onClick={() => {
            setModalState(EVENT_MODAL_STATES.fullyDelete);
            setIsModalVisible(true);
          }}
          className="pressable table_selection_bar_action"
        >
          Удалить полностью
        </div>
      </div>
    ) : null
  ), [selectedEvents, events]);

  const handlePage = (newVal) => {
    if (newVal === page) return;
    setPage(newVal);
    getEvents({ paramsPage: newVal });
  };

  const onCreateEventClick = () => {
    navigate('/events/create');
  };

  useEffect(() => {
    Api.getEventCategories()
      .then(({ data }) => {
        const categories = (data?.categories || [])
          .slice()
          .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
          .map((category) => ({ id: category._id, name: category.name }));

        setCategoryOptions([
          ...categories,
          { id: EVENT_CATEGORY_NONE, name: 'Без категории' },
        ]);
      })
      .catch((error) => {
        console.warn(error, 'while loading event categories');
      });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      getEvents({ paramsPage: 1, paramsPerPage: perPage < 1 ? 1 : perPage > 500 ? 500 : perPage });
      if (perPage < 1) setPerPage(1);
      else if (perPage > 500) setPerPage(500);
    }, 600);

    return () => clearTimeout(timer);
  }, [perPage]);

  useEffect(() => {
    setPage(1);
    getEvents({ paramsPage: 1 });
  }, [selectedFilter]);

  useEffect(() => {
    getEvents({ paramsPage: page, isInitRequired: false });
    setTimeout(() => {
      setIsInit(true);
    }, 500);
  }, []);

  useEffect(() => {
    setItemsPassed((page - 1) * perPage);
  }, [events, page, perPage]);

  return (
    <div className="events_page">
      <PageTitle
        text="Мероприятия (во всех странах)"
        RightComponent={(
          <MyButton
            text="Добавить"
            svgPath={AddOrgIcon}
            className="org_add_btn"
            onClick={onCreateEventClick}
          />
        )}
      />

      <MyTable
        q={q}
        setQ={setQ}
        perPage={perPage}
        setPerPage={setPerPage}
        filters={EVENT_FILTERS}
        filtersCount={{
          [EVENT_FILTERS[0].id]: eventsAllTotal || 0,
          [EVENT_FILTERS[1].id]: eventsActiveTotal || 0,
          [EVENT_FILTERS[2].id]: eventsBlockedTotal || 0,
        }}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        extraFilters={extraFilters}
        Item={TableItemComponent}
        WhileSelectingComponents={WhileSelectingComponents}
        data={events}
        page={page}
        setPage={handlePage}
        total={eventsTotal}
        onSearch={handleSearch}
        isLoading={eventsLoading}
        onCancelSearch={handleCancelSearch}
        showDeepSearch
        deepSearch={deepSearch}
        setDeepSearch={setDeepSearch}
      />

      <EventModal
        selectedEvent={selectedEvent}
        modalState={modalState}
        isVisible={isModalVisible}
        setIsVisible={setIsModalVisible}
        setSelectedEvent={setSelectedEvent}
        selectedEvents={selectedEvents}
        setSelectedEvents={setSelectedEvents}
        getEvents={() => {
          setPage(1);
          getEvents({ paramsPage: 1, isInitRequired: false });
        }}
      />

      <BulkEditSelectedModal
        isOpen={isBulkEditOpen}
        onClose={() => setIsBulkEditOpen(false)}
        entityType="event"
        entityLabel="мероприятий"
        selectedItems={events.filter((item) => selectedEvents.includes(item._id))}
        onFinished={() => {
          setSelectedEvents([]);
          getEvents({ paramsPage: page, isInitRequired: false });
        }}
      />
    </div>
  );
}

export default Events;
